// Vue & Plugins
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ComputedRef, Ref, capitalize, computed, ref, watch } from 'vue'

import { debouncedRef } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
// Other
import { DateTime, Interval } from 'luxon'

import { useResourceLabel } from '../../common/composables/resource-names'

import { Area } from '../../areas/models/area'
import { Building } from '../../buildings/models/building'
// Models
import { DEFAULT_PER_PAGE } from '../../common/models/pagination'
import { ShiftOfferStatus } from '../../employee_shift/models/employeeShiftOffer'
import { Employee } from '../../employees/models/employee'
import { JobRole } from '../../job_roles/models/jobRole'
import { Skill } from '../../skills/models/skill'

export const VIEW_DURATIONS = {
  DAY: { label: 'Day', key: 'day', unit: 'day', count: 1 },
  WEEK: { label: 'Week', key: 'week', unit: 'week', count: 1 },
  TWO_WEEKS: { label: '2 Weeks', key: 'two_weeks', unit: 'week', count: 2 },
  FOUR_WEEKS: { label: '4 Weeks', key: 'four_weeks', unit: 'week', count: 4 },
  MONTH: { label: 'Month', key: 'month', unit: 'month', count: 1 },
} as const

export const viewDurationOptions = Object.values(VIEW_DURATIONS)

type ViewDuration = (typeof VIEW_DURATIONS)[keyof typeof VIEW_DURATIONS]

export const VIEW_GROUPS = {
  AREA: { label: 'Area', resource: 'area' },
  EMPLOYEE: { label: 'Employee', resource: 'employee' },
} as const

type ViewGroup = { label: string; resource: 'area' | 'employee' }

/** Whose rows the schedule shows; managers can hold shifts from the old system */
export type ScheduleEmployeeRole = 'staff' | 'manager' | 'all'

export const scheduleEmployeeRoleOptions: Array<{
  label: string
  value: ScheduleEmployeeRole
}> = [
  { label: 'Staff', value: 'staff' },
  { label: 'Managers', value: 'manager' },
  { label: 'Everyone', value: 'all' },
]

export const useScheduleFilterStore = defineStore('ScheduleFilterStore', () => {
  const { singular: areaSingular } = useResourceLabel('areas')
  const { singular: employeeSingular } = useResourceLabel('employees')

  const viewDurationParam = useRouteQuery<string | null>('duration', null)
  const viewDuration = computed({
    get: () => {
      return (
        viewDurationOptions.find(
          (option) => option.key === viewDurationParam.value,
        ) ?? VIEW_DURATIONS.WEEK
      )
    },
    set: (newDuration: ViewDuration) => {
      viewDurationParam.value = newDuration.key
    },
  })

  const isMonthView = computed(
    () => viewDuration.value.unit === 'month' && viewDuration.value.count === 1,
  )

  const viewGroupOptions = computed<ViewGroup[]>(() => {
    return [
      {
        label: capitalize(areaSingular.value),
        resource: 'area',
      },
      {
        label: capitalize(employeeSingular.value),
        resource: 'employee',
      },
    ]
  })
  const viewGroupParam = useRouteQuery<string | null>('group', null)
  const viewGroup = computed({
    get: () => {
      return (
        viewGroupOptions.value.find(
          (group) => group.resource === viewGroupParam.value,
        ) ?? VIEW_GROUPS.EMPLOYEE
      )
    },
    set: (newGroup: ViewGroup) => {
      viewGroupParam.value = newGroup.resource
    },
  })

  const dateFromParam = useRouteQuery<string | null>('date_from', null)
  const dateFrom = computed<DateTime>({
    get: () => {
      if (dateFromParam.value) {
        const parsed = DateTime.fromISO(dateFromParam.value, { zone: 'utc' })
        if (parsed.isValid) {
          return parsed
        } else {
          console.warn('Invalid date_from query param:', dateFromParam.value)
        }
      }

      return DateTime.now().startOf('week')
    },
    set: (newDate: DateTime) => {
      dateFromParam.value = newDate.toISODate()
    },
  })

  const dateTo = computed(() => {
    const unit = viewDuration.value.unit
    const count = viewDuration.value.count

    let dateTo = dateFrom.value
    if (count > 1) {
      dateTo = dateTo.plus({ [unit]: count - 1 })
    }

    return dateTo.endOf(unit)
  })

  const building: Ref<Building | null> = ref(null)
  /** Narrows both the shifts and the employee rows */
  const jobRole: Ref<JobRole | null> = ref(null)
  /** Cancelled shifts stay off the calendar unless asked for */
  const showCancelledShifts = ref(false)
  const employee: Ref<Employee | null> = ref(null)
  const employeeOfferStatus: ComputedRef<Extract<
    ShiftOfferStatus,
    'accepted'
  > | null> = computed(() => (employee.value ? 'accepted' : null))

  const name = ref('')
  const perPage = ref(DEFAULT_PER_PAGE)
  const page = ref(1)

  const setDate = (newDate: DateTime) => {
    const unit = viewDuration.value.unit
    dateFrom.value = newDate.startOf(unit)
  }

  const nextDate = () => {
    const unit = viewDuration.value.unit
    const count = viewDuration.value.count

    dateFrom.value = dateFrom.value.plus({ [unit]: count }).startOf(unit)
  }

  const prevDate = () => {
    const unit = viewDuration.value.unit
    const count = viewDuration.value.count

    dateFrom.value = dateFrom.value.minus({ [unit]: count }).startOf(unit)
  }

  const dates = computed(() => {
    const interval = Interval.fromDateTimes(dateFrom.value, dateTo.value)

    const dates: DateTime[] = []
    let currentDate = interval.start.startOf('day')
    while (currentDate < interval.end) {
      dates.push(currentDate)

      const isDayView =
        viewDuration.value.unit === 'day' && viewDuration.value.count === 1
      const step = isDayView ? { hours: 1 } : { days: 1 }
      currentDate = currentDate.plus(step)
    }
    return dates
  })

  watch(viewDuration, () => {
    setDate(dateFrom.value)
  })

  const employeeSortDirectionToggle = ref(true)
  const employeeSortDirection: ComputedRef<'asc' | 'desc'> = computed(() => {
    return employeeSortDirectionToggle.value ? 'asc' : 'desc'
  })

  const employeeNameInput = ref('')
  const employeeName = debouncedRef(employeeNameInput, 300)
  const employeeAvailableStartDate = ref<DateTime | null>(null)
  const employeeAvailableEndDate = ref<DateTime | null>(null)
  const employeeSkills = ref<Skill[]>([])
  const employeePerPage = ref(25)
  const employeeRole = ref<ScheduleEmployeeRole>('staff')
  const employeeShowArchived = ref(false)

  const selectEmployee = (selectedEmployee: Employee) => {
    employee.value = selectedEmployee
  }

  const clearEmployee = () => {
    employee.value = null
  }

  const newShiftSelectedStartTime = ref<DateTime | null>(null)
  const newShiftSelectedEndTime = ref<DateTime | null>(null)
  const newShiftSelectedSkills = ref<Skill[]>([])
  const newShiftSelectedArea = ref<Area | null>(null)

  return {
    building,
    jobRole,
    showCancelledShifts,
    employee,
    name,
    perPage,
    page,
    viewDuration,
    isMonthView,
    viewGroupOptions,
    viewGroup,
    dateFrom,
    dateTo,
    dates,
    setDate,
    nextDate,
    prevDate,
    // Employees
    employeeOfferStatus,
    employeeSortDirection,
    employeeSortDirectionToggle,
    employeeNameInput,
    employeeName,
    employeeAvailableStartDate,
    employeeAvailableEndDate,
    employeeSkills,
    employeePerPage,
    employeeRole,
    employeeShowArchived,
    // New Shift Selection
    newShiftSelectedStartTime,
    newShiftSelectedEndTime,
    newShiftSelectedSkills,
    newShiftSelectedArea,
    selectEmployee,
    clearEmployee,
  }
})

if (import.meta.hot !== null && import.meta.hot !== undefined) {
  import.meta.hot.accept(
    acceptHMRUpdate(useScheduleFilterStore, import.meta.hot),
  )
}
